import AsyncStorage from '@react-native-async-storage/async-storage';
import { Platform } from 'react-native';

const REFERRAL_STORAGE_KEY = 'vuelto_ref';
const REFERRAL_CLIENT_STORAGE_DAYS = 90;
const REFERRAL_CODE_RE = /^(?!-)(?!.*--)[A-Z0-9-]{3,20}(?<!-)$/;

export type ReferralStoragePayload = {
  code: string;
  expiresAt: string;
};

function normalizeCode(raw: string): string {
  return raw.trim().toUpperCase();
}

function isValidCode(code: string): boolean {
  return REFERRAL_CODE_RE.test(code);
}

function buildPayload(code: string): ReferralStoragePayload {
  const expires = new Date();
  expires.setUTCDate(expires.getUTCDate() + REFERRAL_CLIENT_STORAGE_DAYS);
  return { code: normalizeCode(code), expiresAt: expires.toISOString() };
}

function parsePayload(raw: string | null): ReferralStoragePayload | null {
  if (!raw?.trim()) return null;
  try {
    const parsed = JSON.parse(raw) as ReferralStoragePayload;
    if (typeof parsed.code !== 'string' || typeof parsed.expiresAt !== 'string') return null;
    if (Date.parse(parsed.expiresAt) <= Date.now()) return null;
    const code = normalizeCode(parsed.code);
    if (!isValidCode(code)) return null;
    return { code, expiresAt: parsed.expiresAt };
  } catch {
    return null;
  }
}

export async function persistReferralCode(code: string): Promise<void> {
  const normalized = normalizeCode(code);
  if (!isValidCode(normalized)) return;
  const payload = buildPayload(normalized);
  const json = JSON.stringify(payload);
  await AsyncStorage.setItem(REFERRAL_STORAGE_KEY, json);
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(REFERRAL_STORAGE_KEY, json);
    } catch {
      /* ignore quota / private mode */
    }
  }
}

export async function readStoredReferralCode(): Promise<string | null> {
  let raw: string | null = null;
  try {
    raw = await AsyncStorage.getItem(REFERRAL_STORAGE_KEY);
  } catch {
    raw = null;
  }
  if (!raw && Platform.OS === 'web' && typeof window !== 'undefined') {
    try {
      raw = window.localStorage.getItem(REFERRAL_STORAGE_KEY);
    } catch {
      raw = null;
    }
  }
  const parsed = parsePayload(raw);
  return parsed?.code ?? null;
}

export async function captureReferralFromUrl(url: string): Promise<void> {
  try {
    const parsed = new URL(url);
    const ref = parsed.searchParams.get('ref');
    if (!ref?.trim()) return;
    await persistReferralCode(ref);
  } catch {
    /* ignore malformed URLs */
  }
}

function readWebCookie(name: string): string | null {
  if (Platform.OS !== 'web' || typeof document === 'undefined') return null;
  const prefix = `${name}=`;
  for (const segment of document.cookie.split(';')) {
    const trimmed = segment.trim();
    if (!trimmed.startsWith(prefix)) continue;
    try {
      return decodeURIComponent(trimmed.slice(prefix.length));
    } catch {
      return null;
    }
  }
  return null;
}

/** Cookie `vuelto_ref` en `.vuelto.ar` (landing www) — respaldo si entraron a app sin `?ref=`. */
export async function captureReferralFromDocumentCookie(): Promise<void> {
  const raw = readWebCookie(REFERRAL_STORAGE_KEY);
  const parsed = parsePayload(raw);
  if (!parsed) return;
  await persistReferralCode(parsed.code);
}

/** URL `ref` gana; si no hay, cookie cross-subdomain; idempotente con storage local. */
export async function captureReferralFromWebContext(url: string): Promise<void> {
  try {
    const parsed = new URL(url);
    if (parsed.searchParams.get('ref')?.trim()) {
      await captureReferralFromUrl(url);
      return;
    }
  } catch {
    /* fall through to cookie */
  }
  await captureReferralFromDocumentCookie();
}
